import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Header from "../../app/universalComponents/Header";
import Footer from "../../app/universalComponents/Footer";
import Loading from "../../app/universalComponents/Loading";
import NotFoundPage from "../not-found/NotFoundPage";
import { api } from "../../app/http/axiosClient";

function getFilenameFromDisposition(contentDisposition) {
    if (!contentDisposition) {
        return "npSolvedTicket.pdf";
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

    if (utf8Match?.[1]) {
        return decodeURIComponent(utf8Match[1]);
    }

    const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);

    if (plainMatch?.[1]) {
        return plainMatch[1];
    }

    return "npSolvedTicket.pdf";
}

function getEmailMessage(emailStatus) {
    if (emailStatus === "sent") {
        return "A confirmation email with another copy of your tickets has also been sent.";
    }

    if (emailStatus === "failed") {
        return "Your payment was successful, but we could not send the email copy.";
    }

    if (emailStatus === "pending") {
        return "Your tickets are ready. We are still sending the email copy.";
    }

    return "Your tickets are ready to download.";
}

export default function PaymentSuccessPage() {
    const [searchParams] = useSearchParams();

    const sessionId = searchParams.get("session_id");

    const [ticketBlob, setTicketBlob] = useState(null);
    const [filename, setFilename] = useState("ticket.pdf");
    const [emailStatus, setEmailStatus] = useState("");
    const [error, setError] = useState(false);
    const [downloadError, setDownloadError] = useState("");

    useEffect(() => {
        async function getTicket() {
            try {
                setError(false);
                setDownloadError("");

                const statusResponse = await api.get(
                    `/payments/status/${encodeURIComponent(sessionId)}`
                );

                const orderStatus = statusResponse.data.order_status;
                const orderEmailStatus = statusResponse.data.email_status;

                if (orderStatus !== "paid") {
                    setError(true);
                    return;
                }

                setEmailStatus(orderEmailStatus);

                const ticketResponse = await api.get(
                    `/public/tickets/download/${encodeURIComponent(sessionId)}`,
                    {
                        responseType: "blob",
                    }
                );

                const contentDisposition = ticketResponse.headers["content-disposition"];
                const resolvedFilename = getFilenameFromDisposition(contentDisposition);

                setTicketBlob(ticketResponse.data);
                setFilename(resolvedFilename);
            } catch (err) {
                setError(true);
            }
        }

        if (sessionId) {
            getTicket();
        } else {
            setError(true);
        }
    }, [sessionId]);

    useEffect(() => {
        document.title = "Payment Successful | Tickets.NP";
    }, []);

    function handleDownload() {
        if (!ticketBlob) {
            setDownloadError("Ticket file is not available.");
            return;
        }

        const objectUrl = window.URL.createObjectURL(ticketBlob);
        const link = document.createElement("a");

        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(objectUrl);
    }

    if (error) {
        return <NotFoundPage />;
    }

    if (!ticketBlob) {
        return <Loading fullPage />;
    }

    return (
        <>
            <div className="flex min-h-screen flex-col">
                <Header sellTickets />

                <main className="flex flex-1 items-center justify-center px-6 py-16">
                    <section className="mx-auto w-full max-w-xl text-center">
                        <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
                            Payment successful
                        </p>

                        <h1 className="mt-4 text-4xl font-bold tracking-tight lg:text-5xl">
                            Your tickets are ready
                        </h1>

                        <hr className="mx-auto my-6 h-px w-56 border-0 bg-gray-300" />

                        <p className="text-base">
                            Thanks for your order. You can download your tickets below.
                        </p>

                        <div className="pt-6">
                            <button
                                type="button"
                                onClick={handleDownload}
                                className="rounded-sm bg-black px-4 py-2 text-lg text-white transition hover:opacity-75"
                            >
                                Download Tickets
                            </button>
                        </div>

                        {downloadError ? (
                            <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                {downloadError}
                            </div>
                        ) : null}

                        <p className={`mx-auto mt-6 max-w-xl text-sm ${emailStatus === "sent" ? "text-gray-500" : "text-red-500"}`}>
                            {getEmailMessage(emailStatus)}
                        </p>
                    </section>
                </main>
            </div>

            <Footer />
        </>
    );
}